(() => {
  const SELECTOR = '[data-lucien-monthly-cards]';
  const ZOOM_TRIGGER_SELECTOR = '[data-monthly-card-zoom]';
  const DRAG_THRESHOLD = 4;
  const DOUBLE_TAP_DELAY = 320;
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const DOUBLE_TAP_SCALE = 2.15;
  const LERP = 0.2;

  let overlay = null;
  let viewport = null;
  let image = null;
  let closeButton = null;
  let lastFocusedElement = null;

  const state = {
    currentScale: 1,
    targetScale: 1,
    currentX: 0,
    currentY: 0,
    targetX: 0,
    targetY: 0,
    raf: null,
    pointers: new Map(),
    hasMoved: false,
    isPanning: false,
    dragStartX: 0,
    dragStartY: 0,
    dragStartTargetX: 0,
    dragStartTargetY: 0,
    pinchStartDistance: 0,
    pinchStartScale: 1,
    pinchLastCenterX: 0,
    pinchLastCenterY: 0,
    lastTapTime: 0,
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const lerp = (from, to, amount) => from + (to - from) * amount;

  const getViewportRect = () => viewport.getBoundingClientRect();

  const getBaseImageSize = () => {
    if (!viewport || !image || !image.naturalWidth || !image.naturalHeight) {
      return { width: 0, height: 0 };
    }

    const imageRect = image.getBoundingClientRect();
    const scale = state.currentScale || MIN_SCALE;

    if (imageRect.width && imageRect.height) {
      return {
        width: imageRect.width / scale,
        height: imageRect.height / scale,
      };
    }

    const rect = getViewportRect();
    const ratio = image.naturalWidth / image.naturalHeight;
    const maxWidth = rect.width * 0.92;
    const maxHeight = rect.height * 0.92;
    let width = maxWidth;
    let height = width / ratio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }

    return { width, height };
  };

  const getBounds = (scale = state.targetScale) => {
    const rect = getViewportRect();
    const base = getBaseImageSize();

    return {
      x: Math.max(0, (base.width * scale - rect.width) / 2),
      y: Math.max(0, (base.height * scale - rect.height) / 2),
    };
  };

  const clampTargetPosition = () => {
    if (state.targetScale <= MIN_SCALE + 0.001) {
      state.targetX = 0;
      state.targetY = 0;
      return;
    }

    const bounds = getBounds(state.targetScale);
    state.targetX = clamp(state.targetX, -bounds.x, bounds.x);
    state.targetY = clamp(state.targetY, -bounds.y, bounds.y);
  };

  const clampCurrentPosition = () => {
    if (state.currentScale <= MIN_SCALE + 0.001) {
      state.currentX = 0;
      state.currentY = 0;
      return;
    }

    const bounds = getBounds(state.currentScale);
    state.currentX = clamp(state.currentX, -bounds.x, bounds.x);
    state.currentY = clamp(state.currentY, -bounds.y, bounds.y);
  };

  const applyTransform = () => {
    if (!image || !viewport) return;

    clampCurrentPosition();
    image.style.setProperty('--zoom-x', `${state.currentX.toFixed(2)}px`);
    image.style.setProperty('--zoom-y', `${state.currentY.toFixed(2)}px`);
    image.style.setProperty('--zoom-scale', state.currentScale.toFixed(4));
    viewport.classList.toggle('is-zoomed', state.targetScale > MIN_SCALE + 0.01);
  };

  const animate = () => {
    state.currentScale = lerp(state.currentScale, state.targetScale, LERP);
    state.currentX = lerp(state.currentX, state.targetX, LERP);
    state.currentY = lerp(state.currentY, state.targetY, LERP);

    const scaleDone = Math.abs(state.currentScale - state.targetScale) < 0.001;
    const xDone = Math.abs(state.currentX - state.targetX) < 0.1;
    const yDone = Math.abs(state.currentY - state.targetY) < 0.1;

    if (scaleDone) state.currentScale = state.targetScale;
    if (xDone) state.currentX = state.targetX;
    if (yDone) state.currentY = state.targetY;

    applyTransform();

    if (scaleDone && xDone && yDone) {
      state.raf = null;
      return;
    }

    state.raf = window.requestAnimationFrame(animate);
  };

  const scheduleAnimation = () => {
    if (state.raf) return;
    state.raf = window.requestAnimationFrame(animate);
  };

  const setTarget = (scale, x = state.targetX, y = state.targetY) => {
    state.targetScale = clamp(scale, MIN_SCALE, MAX_SCALE);
    state.targetX = x;
    state.targetY = y;
    clampTargetPosition();
    scheduleAnimation();
  };

  const zoomAroundPoint = (clientX, clientY, nextScale) => {
    const rect = getViewportRect();
    const pointX = clientX - rect.left - rect.width / 2;
    const pointY = clientY - rect.top - rect.height / 2;
    const oldScale = state.targetScale || MIN_SCALE;
    const newScale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    const ratio = newScale / oldScale;

    let nextX = pointX - (pointX - state.targetX) * ratio;
    let nextY = pointY - (pointY - state.targetY) * ratio;

    if (newScale <= MIN_SCALE + 0.001) {
      nextX = 0;
      nextY = 0;
    }

    setTarget(newScale, nextX, nextY);
  };

  const resetViewer = (immediate = false) => {
    state.targetScale = MIN_SCALE;
    state.targetX = 0;
    state.targetY = 0;
    state.pointers.clear();
    state.hasMoved = false;
    state.isPanning = false;
    state.pinchStartDistance = 0;
    state.lastTapTime = 0;

    if (viewport) viewport.classList.remove('is-panning', 'is-zoomed');

    if (immediate) {
      if (state.raf) {
        window.cancelAnimationFrame(state.raf);
        state.raf = null;
      }

      state.currentScale = MIN_SCALE;
      state.currentX = 0;
      state.currentY = 0;
      applyTransform();
      return;
    }

    scheduleAnimation();
  };

  const toggleZoomAroundEvent = (event) => {
    if (!viewport || !image || !image.complete) return;

    event.preventDefault();
    const nextScale = state.targetScale > MIN_SCALE + 0.01 ? MIN_SCALE : DOUBLE_TAP_SCALE;
    zoomAroundPoint(event.clientX, event.clientY, nextScale);
  };

  const closeZoom = () => {
    if (!overlay || overlay.hidden) return;

    overlay.hidden = true;
    document.documentElement.classList.remove('lucien-monthly-cards-zoom-open');
    document.body.classList.remove('lucien-monthly-cards-zoom-open');
    resetViewer(true);

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus({ preventScroll: true });
    }

    lastFocusedElement = null;
  };

  const getPointerDistance = (first, second) => Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);

  const getPointerCenter = (first, second) => ({
    x: (first.clientX + second.clientX) / 2,
    y: (first.clientY + second.clientY) / 2,
  });

  const prepareSinglePointerDrag = (pointer) => {
    state.dragStartX = pointer.clientX;
    state.dragStartY = pointer.clientY;
    state.dragStartTargetX = state.targetX;
    state.dragStartTargetY = state.targetY;
    state.hasMoved = false;
  };

  const preparePinch = () => {
    const pointers = Array.from(state.pointers.values());
    if (pointers.length < 2) return;

    const center = getPointerCenter(pointers[0], pointers[1]);
    state.pinchStartDistance = getPointerDistance(pointers[0], pointers[1]);
    state.pinchStartScale = state.targetScale;
    state.pinchLastCenterX = center.x;
    state.pinchLastCenterY = center.y;
  };

  const handleViewportPointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    event.preventDefault();
    viewport.setPointerCapture(event.pointerId);
    state.pointers.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });

    state.isPanning = state.targetScale > MIN_SCALE + 0.01;
    viewport.classList.toggle('is-panning', state.isPanning);

    if (state.pointers.size === 1) {
      prepareSinglePointerDrag(event);
    } else if (state.pointers.size === 2) {
      preparePinch();
      state.isPanning = true;
      viewport.classList.add('is-panning');
    }
  };

  const handleViewportPointerMove = (event) => {
    if (!state.pointers.has(event.pointerId)) return;

    event.preventDefault();
    state.pointers.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });

    const pointers = Array.from(state.pointers.values());

    if (pointers.length >= 2) {
      const distance = getPointerDistance(pointers[0], pointers[1]);
      if (!state.pinchStartDistance) return;

      const center = getPointerCenter(pointers[0], pointers[1]);
      const centerDeltaX = center.x - state.pinchLastCenterX;
      const centerDeltaY = center.y - state.pinchLastCenterY;
      const nextScale = state.pinchStartScale * (distance / state.pinchStartDistance);

      state.targetX += centerDeltaX;
      state.targetY += centerDeltaY;
      state.pinchLastCenterX = center.x;
      state.pinchLastCenterY = center.y;
      zoomAroundPoint(center.x, center.y, nextScale);
      state.hasMoved = true;
      return;
    }

    const pointer = pointers[0];
    const deltaX = pointer.clientX - state.dragStartX;
    const deltaY = pointer.clientY - state.dragStartY;

    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      state.hasMoved = true;
    }

    if (state.targetScale <= MIN_SCALE + 0.01) return;

    state.targetX = state.dragStartTargetX + deltaX;
    state.targetY = state.dragStartTargetY + deltaY;
    clampTargetPosition();
    scheduleAnimation();
  };

  const finishPointer = (event) => {
    if (!state.pointers.has(event.pointerId)) return;

    const wasTap = !state.hasMoved;
    state.pointers.delete(event.pointerId);
    state.isPanning = false;
    viewport.classList.remove('is-panning');

    if (state.pointers.size === 1) {
      prepareSinglePointerDrag(Array.from(state.pointers.values())[0]);
    } else if (state.pointers.size === 0 && wasTap && event.target === image) {
      const now = Date.now();

      if (now - state.lastTapTime < DOUBLE_TAP_DELAY) {
        const nextScale = state.targetScale > MIN_SCALE + 0.01 ? MIN_SCALE : DOUBLE_TAP_SCALE;
        zoomAroundPoint(event.clientX, event.clientY, nextScale);
        state.lastTapTime = 0;
      } else {
        state.lastTapTime = now;
      }
    }

    if (state.targetScale <= MIN_SCALE + 0.01) {
      setTarget(MIN_SCALE, 0, 0);
    } else {
      clampTargetPosition();
      scheduleAnimation();
    }
  };

  const createZoomOverlay = () => {
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.className = 'lucien-monthly-cards-zoom';
    overlay.hidden = true;
    overlay.innerHTML = `
      <div class="lucien-monthly-cards-zoom__backdrop" data-monthly-zoom-close aria-hidden="true"></div>
      <div class="lucien-monthly-cards-zoom__dialog" role="dialog" aria-modal="true" aria-label="Image preview">
        <button class="lucien-monthly-cards-zoom__close" type="button" data-monthly-zoom-close aria-label="Close image"></button>
        <div class="lucien-monthly-cards-zoom__viewport" data-monthly-zoom-viewport>
          <img class="lucien-monthly-cards-zoom__image" alt="" draggable="false">
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    image = overlay.querySelector('.lucien-monthly-cards-zoom__image');
    viewport = overlay.querySelector('[data-monthly-zoom-viewport]');
    closeButton = overlay.querySelector('.lucien-monthly-cards-zoom__close');

    overlay.querySelectorAll('[data-monthly-zoom-close]').forEach((element) => {
      element.addEventListener('click', closeZoom);
    });

    image.addEventListener('dragstart', (event) => event.preventDefault());
    image.addEventListener('load', () => resetViewer(true));

    viewport.addEventListener('dragstart', (event) => event.preventDefault());
    viewport.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        const factor = Math.exp(-event.deltaY * 0.0008);
        zoomAroundPoint(event.clientX, event.clientY, state.targetScale * factor);
      },
      { passive: false }
    );

    viewport.addEventListener('pointerdown', handleViewportPointerDown);
    viewport.addEventListener('pointermove', handleViewportPointerMove);
    viewport.addEventListener('pointerup', finishPointer);
    viewport.addEventListener('pointercancel', finishPointer);
    viewport.addEventListener('dblclick', toggleZoomAroundEvent);

    window.addEventListener('resize', () => {
      clampTargetPosition();
      scheduleAnimation();
    });

    document.addEventListener('keydown', (event) => {
      if (!overlay || overlay.hidden) return;

      if (event.key === 'Escape') {
        closeZoom();
        return;
      }

      if (event.key === 'Tab' && closeButton) {
        event.preventDefault();
        closeButton.focus({ preventScroll: true });
      }
    });

    return overlay;
  };

  const openZoom = (trigger) => {
    const src = trigger.dataset.zoomSrc;
    if (!src) return;

    createZoomOverlay();
    lastFocusedElement = trigger;
    overlay.hidden = false;
    document.documentElement.classList.add('lucien-monthly-cards-zoom-open');
    document.body.classList.add('lucien-monthly-cards-zoom-open');
    resetViewer(true);

    image.alt = trigger.dataset.zoomAlt || '';
    image.src = src;

    if (closeButton) closeButton.focus({ preventScroll: true });
  };

  const initZoomTriggers = (container = document) => {
    container.querySelectorAll(ZOOM_TRIGGER_SELECTOR).forEach((trigger) => {
      if (trigger.dataset.monthlyCardZoomReady === 'true') return;

      trigger.dataset.monthlyCardZoomReady = 'true';
      trigger.addEventListener('dragstart', (event) => event.preventDefault());
      trigger.querySelectorAll('img').forEach((img) => {
        img.setAttribute('draggable', 'false');
        img.addEventListener('dragstart', (event) => event.preventDefault());
      });
      trigger.addEventListener('click', () => openZoom(trigger));
    });
  };

  const initSlider = (slider) => {
    if (!slider || slider.dataset.lucienMonthlyCardsReady === 'true') return;

    slider.dataset.lucienMonthlyCardsReady = 'true';

    let isPointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let activeZoomTrigger = null;

    const stopDragging = (event, shouldOpenZoom = false) => {
      if (!isPointerDown) return;

      const zoomTrigger = activeZoomTrigger;
      const wasDragging = isDragging;

      isPointerDown = false;
      activeZoomTrigger = null;
      slider.classList.remove('is-dragging');

      if (shouldOpenZoom && zoomTrigger && !wasDragging) {
        event.preventDefault();
        event.stopPropagation();
        openZoom(zoomTrigger);
      }

      window.setTimeout(() => {
        isDragging = false;
      }, 0);
    };

    slider.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;

      isPointerDown = true;
      isDragging = false;
      activeZoomTrigger = event.target.closest(ZOOM_TRIGGER_SELECTOR);
      startX = event.clientX;
      startScrollLeft = slider.scrollLeft;
      slider.setPointerCapture(event.pointerId);
    });

    slider.addEventListener('pointermove', (event) => {
      if (!isPointerDown) return;

      const deltaX = event.clientX - startX;

      if (Math.abs(deltaX) > DRAG_THRESHOLD) {
        isDragging = true;
        slider.classList.add('is-dragging');
      }

      if (isDragging) {
        event.preventDefault();
        slider.scrollLeft = startScrollLeft - deltaX;
      }
    });

    slider.addEventListener('pointerup', (event) => stopDragging(event, true));
    slider.addEventListener('pointercancel', (event) => stopDragging(event, false));
    slider.addEventListener('pointerleave', (event) => stopDragging(event, false));

    slider.addEventListener(
      'click',
      (event) => {
        if (!isDragging) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  };

  const init = (container = document) => {
    container.querySelectorAll(SELECTOR).forEach(initSlider);
    initZoomTriggers(container);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
